import { definedMap } from "@glideapps/ts-necessities";

import {
    ComponentKind,
    ComponentDescription,
    getPrimitiveColumns,
    PropertyDescription,
    TableGlideType,
    InputOutputTables,
    isPrimitiveType,
    TableColumn,
    MutatingScreenKind,
    getTableColumnDisplayName,
    makeEmptyComponentDescription,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { SerializedElementBuilder } from "../serialized-component";
import { ComponentDescriptor, ComponentBuilder, ComponentConfiguratorContext, PropertyDescriptor } from ".";
import { ComponentHandlerBase } from "./handler";
import { getColumnProperty, makeColumnProperty, makeStringProperty } from "./property-utils";
import {
    makePlaceholderPropertyDescriptor,
    makePrimaryKeyPropertyHandler,
    makeTextPropertyDescriptor,
} from "./descriptor-utils";
import { ColumnPropertyHandler, ColumnPropertyFlag, isRequiredPropertyHandler } from "./property-handlers";
import { assert } from "../../Support";
import { PropertySection } from "./property-sections";
import { makeText, makeValidationFlagForProperty, setColumnInRowForEditComponent } from "../builder-utils";
import { getDocURL } from "../../docUrl";

const ComponentKindNotes: ComponentKind = "notes";

const propertyNamePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.Searchable,
        ColumnPropertyFlag.EditedInApp,
    ],
    undefined,
    ["notes", "note"],
    getPrimitiveColumns,
    "string",
    PropertySection.Data
);

export interface NotesComponentDescription extends ComponentDescription {
    readonly propertyName: PropertyDescription;
    readonly primaryKeyProperty: PropertyDescription | undefined;
    readonly placeholder: PropertyDescription;
    readonly title: PropertyDescription;
}

export class NotesComponentHandler extends ComponentHandlerBase<NotesComponentDescription> {
    constructor() {
        super(ComponentKindNotes);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(desc: NotesComponentDescription): boolean {
        return isRequiredPropertyHandler.getSwitch(desc);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: NotesComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables.output,
            definedMap(desc.primaryKeyProperty, getColumnProperty),
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        return b.let(
            [...lets, ["title", makeText(builder, desc.title, tables.input) ?? b.localizedString("notes")]],
            b.ui("Note", {
                text: valueToModify,
                onTap: definedMap(action, a =>
                    builder.makePushOverlayEditorScreen(
                        valueToModify,
                        b.getVariable("title"),
                        makeText(builder, desc.placeholder, tables.input, true) ?? b.localizedString("writeANote"),
                        b.localizedString("done"),
                        b.constant("check"),
                        b.constant("platform-accept"),
                        true,
                        b.bindContextToValue(a, b.getContext)
                    )
                ),
                isEnabled,
                isRequired: b.constant(isRequiredPropertyHandler.getSwitch(desc)),
                title: b.getVariable("title"),
            })
        );
    }

    public makeValidationFlag(
        b: SerializedElementBuilder,
        desc: ComponentDescription,
        outputContext: SerializedComponent,
        table: TableGlideType
    ): SerializedComponent | undefined {
        return makeValidationFlagForProperty(b, outputContext, propertyNamePropertyHandler.getColumnName(desc), table);
    }

    public getDescriptor(
        _desc: NotesComponentDescription,
        table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<NotesComponentDescription> {
        const properties: PropertyDescriptor[] = [
            propertyNamePropertyHandler,
            ...makePrimaryKeyPropertyHandler(ccc, table),
            makeTextPropertyDescriptor("title", "Title", "Enter title", true, mutatingScreenKind, {
                searchable: false,
            }),
            makePlaceholderPropertyDescriptor(mutatingScreenKind, PropertySection.Data),
            ...this.getBasePropertyDescriptors(),
        ];
        if (mutatingScreenKind !== undefined) {
            properties.push(isRequiredPropertyHandler);
        }
        return {
            name: "Notes",
            description: "Text that you can read and edit",
            img: "componentNotes",
            group: "Entry Fields",
            helpUrl: getDocURL("notes"),
            properties,
        };
    }

    public static defaultComponent(column: TableColumn): NotesComponentDescription {
        assert(isPrimitiveType(column.type));
        return {
            ...makeEmptyComponentDescription(ComponentKindNotes),
            kind: ComponentKindNotes,
            propertyName: makeColumnProperty(column.name),
            primaryKeyProperty: undefined,
            placeholder: makeStringProperty(""),
            title: makeStringProperty(getTableColumnDisplayName(column)),
        };
    }
}
